import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Maximize2, Minus, Plus } from 'lucide-react';
import { Segmented, btnSmall } from '@/components/svg-tools/ToolParts';
import { svgImageSrc } from '@/utils/svg-tools/svg-source';

export type PreviewBackground = 'light' | 'dark' | 'checker';

const BACKGROUNDS: { value: PreviewBackground; label: string }[] = [
  { value: 'checker', label: 'Checker' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

const BG_CLASS: Record<PreviewBackground, string> = {
  light: 'bg-white',
  dark: 'bg-[#16181C]',
  checker: 'checker-bg',
};

const ZOOMS = [0.1, 0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4, 6, 8, 12, 16];

/**
 * Draws the SVG as an <img> from a data URL. As an image the SVG can't run scripts or event
 * handlers and can't load other files, so pasted code is safe to show. (A blob: URL would
 * carry this site's origin if someone opened it in a new tab; a data: URL does not.)
 */
export function SvgPreview({
  svg,
  width,
  height,
  empty,
  heightClass = 'h-[280px] sm:h-[420px]',
  toolbarExtra,
}: {
  /** Normalized SVG text, or null when there is nothing valid to show. */
  svg: string | null;
  width: number;
  height: number;
  empty: ReactNode;
  heightClass?: string;
  toolbarExtra?: ReactNode;
}) {
  const [bg, setBg] = useState<PreviewBackground>('checker');
  const [zoom, setZoom] = useState<number | 'fit'>('fit');
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const src = useMemo(() => (svg ? svgImageSrc(svg) : null), [svg]);

  useEffect(() => {
    const el = areaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => setBox({ w: entry.contentRect.width, h: entry.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fit = box.w && box.h ? Math.min((box.w - 32) / width, (box.h - 32) / height) : 1;
  const scale = Math.max(0.01, zoom === 'fit' ? fit : zoom);
  const step = (dir: 1 | -1) => {
    const next = dir > 0 ? ZOOMS.find((z) => z > scale * 1.001) : [...ZOOMS].reverse().find((z) => z < scale * 0.999);
    setZoom(next ?? (dir > 0 ? ZOOMS[ZOOMS.length - 1] : ZOOMS[0]));
  };
  // Keep a huge zoom from asking the browser for a gigapixel image.
  const drawW = Math.min(width * scale, 8000);
  const drawH = (drawW / width) * height;

  return (
    <div className="flex min-w-0 flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-cream-200">
        <Segmented label="Preview background" value={bg} options={BACKGROUNDS} onChange={setBg} />
        <div className="flex items-center gap-1.5">
          {toolbarExtra}
          <button type="button" className={`${btnSmall} w-8 px-0`} onClick={() => step(-1)} aria-label="Zoom out">
            <Minus className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="h-8 min-w-[52px] rounded-md px-1 text-[12.5px] font-mono text-ink-soft hover:bg-cream-100"
            onClick={() => setZoom(1)}
            title="Actual size"
            aria-label={`Zoom ${Math.round(scale * 100)} percent. Set to 100 percent`}
          >
            {Math.round(scale * 100)}%
          </button>
          <button type="button" className={`${btnSmall} w-8 px-0`} onClick={() => step(1)} aria-label="Zoom in">
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`${btnSmall} w-8 px-0 ${zoom === 'fit' ? 'border-sky/50 text-sky' : ''}`}
            onClick={() => setZoom('fit')}
            aria-label="Fit to the preview"
            title="Fit"
          >
            <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div ref={areaRef} className={`relative min-w-0 overflow-auto ${heightClass} ${BG_CLASS[bg]}`}>
        {src && failedSrc !== src ? (
          <div className="flex min-h-full min-w-full w-max items-center justify-center p-4">
            {/* eslint-disable-next-line @next/next/no-img-element -- a data: URL of the visitor's SVG */}
            <img
              key={src}
              src={src}
              alt="Preview of the SVG"
              width={drawW}
              height={drawH}
              draggable={false}
              onError={() => setFailedSrc(src)}
              className="block max-w-none"
              style={{ width: drawW, height: drawH }}
            />
          </div>
        ) : (
          <div className="flex h-full items-center justify-center p-6 text-center text-[13px] leading-relaxed text-ink-muted">
            {src ? 'The browser could not draw this SVG.' : empty}
          </div>
        )}
      </div>
    </div>
  );
}
